import React, { useState, useEffect } from "react";
import { Row, Col, Button, Dropdown, ButtonGroup, Form } from "react-bootstrap";
import { TfiAnnouncement } from "react-icons/tfi";
import { TbEyeStar } from "react-icons/tb";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import axios from "axios";
import moment from "moment"; // For date formatting
import "./dept-announcement.css";

// Styled Dialog Box for fancy design

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'; // Fallback for missing dates

  const date = new Date(dateString);
  if (isNaN(date)) return 'Invalid Date'; // Handle invalid date formats

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

// Styled Dialog component
const CustomDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90%',
    margin: '10px',
    zIndex: theme.zIndex.drawer + 1,
  },
}));

// Styles for Dialog components
const dialogTitleStyle = {
  fontSize: '1.5rem',
  position: 'relative',
  paddingRight: '40px',
};

const dialogContentStyle = {
  fontSize: '1rem',
  lineHeight: '1.5',
  padding: '1.5rem',
};

const closeIconStyle = {
  position: 'absolute',
  right: 0,
  top: 0,
  margin: '10px',
  color: '#fff',
  fontSize: '1.5rem',
};

// Fancy Dialog Component with smooth transition and animation
function ResponsiveDialog({ open, onClose, announcement }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <CustomDialog
    fullScreen={fullScreen}
    open={open}
    onClose={onClose}
    aria-labelledby="responsive-dialog-title"
  >
    <DialogTitle id="responsive-dialog-title" style={dialogTitleStyle} className='header-announcement'>
      {announcement?.title || 'Announcement'}
      <IconButton
        edge="end"
        color="inherit"
        onClick={onClose}
        style={closeIconStyle}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent style={dialogContentStyle}>
      <DialogContentText>
        <strong>Title:</strong> {announcement?.title} <br />
        <strong>Message:</strong> {announcement?.content} <br />
        <strong>For:</strong> {announcement?.category} <br />
        <strong>Admin</strong><br />
        <strong>Date:</strong> {formatDateTime(announcement?.createdAt)}
        <hr />
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button autoFocus onClick={onClose}>
        Close
      </Button>
    </DialogActions>
  </CustomDialog>
  );
}

export default function DeptAnnouncement() {
  const rowsPerPage = 10;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const userDepartment = localStorage.getItem("department");

  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!userDepartment) {
        console.error("User department not found in session storage.");
        return;
      }

      try {
        const response = await axios.get(
          "https://backend.ems.uk.hiradentallab.com/api/deptannouncements/by-department",
          {
            params: { department: userDepartment },
          }
        );
        setData(response.data);
        setFilteredData(response.data);
        setTotalPages(Math.ceil(response.data.length / rowsPerPage));
      } catch (error) {
        console.error("Error fetching announcements:", error);
      }
    };

    fetchAnnouncements();
  }, [userDepartment]);

  const handleSearch = (e) => {
    e.preventDefault();
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = data.filter((item) => {
      const title = item.title ? item.title.toLowerCase() : "";
      const message = item.message ? item.message.toLowerCase() : "";
      const date = item.createdAt
        ? moment(item.createdAt).format("MMM Do YYYY").toLowerCase()
        : "";

      return (
        title.includes(query) || message.includes(query) || date.includes(query)
      );
    });

    setFilteredData(filtered);
    setTotalPages(Math.ceil(filtered.length / rowsPerPage));
    setCurrentPage(1);
  };

  const handleClickOpen = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const range = [];
    for (let i = 1; i <= totalPages; i++) {
      range.push(i);
    }
    return range;
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="container-fluid">
      <h1 className="announcement-header">
        <TfiAnnouncement style={{ marginRight: "2px", color: "#6c757d" }} />
        Announcement
      </h1>
      <hr />

      <div className="announcement-table-container">
        <div className="available-leaves-actions">
          <Row>
            <Col xs={12} md={6} className="d-flex align-items-center mb-3">
              <Dropdown as={ButtonGroup}>
                <Button variant="primary">Show Entries</Button>
                <Dropdown.Toggle
                  split
                  variant="primary"
                  id="dropdown-split-basic"
                />
                <Dropdown.Menu>
                  <Dropdown.Item>10</Dropdown.Item>
                  <Dropdown.Item>25</Dropdown.Item>
                  <Dropdown.Item>50</Dropdown.Item>
                  <Dropdown.Item>100</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
              <Form className="d-flex" onSubmit={handleSearch}>
                <Form.Control
                  type="search"
                  placeholder="Search...."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="search-bar"
                />
              </Form>
            </Col>
          </Row>
        </div>

        <div className="table-responsive">
          <table className="announcement-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Message</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  <tr key={item._id}>
                    <td>{item.title}</td>
                    <td>{item.message}</td>
                    <td>
                      {moment(item.createdAt).format("MMM Do YYYY, h:mm:ss a")}
                    </td>
                    <td>
                      <Button
                        onClick={() => handleClickOpen(item)}
                        size="sm"
                        variant="primary"
                      >
                        <TbEyeStar />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">No data available</td>
                </tr>
              )}
            </tbody>
            <tfoot className="table-footer">
              <tr className="announcement-table-footer">
                <td>Total</td>
                <td>{data.length}</td>
                <td>-</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="pagination-container">
          <div className="pagination-info">
            Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
            {Math.min(currentPage * rowsPerPage, data.length)} of {data.length}{" "}
            entries
          </div>
          <nav>
            <ul className="pagination">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
              </li>
              {pageRange().map((page) => (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Responsive Dialog */}
      <ResponsiveDialog
        open={dialogOpen}
        onClose={handleClose}
        announcement={selectedAnnouncement}
      />
    </div>
  );
}
